import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Users, Search, Loader2, Home, Phone, Mail, MapPin, Calendar, Repeat, Zap, CheckCircle, Clock, XCircle, CreditCard, Plus, ChevronDown, ChevronUp, Gift, ArrowRight, X, Navigation } from 'lucide-react';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Address Search (Google Places) ───────────────────────────────────────────
const AddressSearchInput = ({ value, onChange, onSelect, placeholder = 'Search address...' }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const autocompleteRef = useRef(null);
  const geocoderRef = useRef(null);
  const selectingRef = useRef(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    const init = setInterval(() => {
      if (window.google?.maps?.places) {
        autocompleteRef.current = new window.google.maps.places.AutocompleteService();
        geocoderRef.current = new window.google.maps.Geocoder();
        clearInterval(init);
      }
    }, 300);
    return () => clearInterval(init);
  }, []);

  const search = useCallback((q) => {
    if (!q || q.length < 3 || !autocompleteRef.current) { setSuggestions([]); return; }
    setSearching(true);
    const timeout = setTimeout(() => setSearching(false), 5000);
    autocompleteRef.current.getPlacePredictions(
      { input: q, componentRestrictions: { country: 'in' }, types: ['geocode', 'establishment'] },
      (predictions, status) => {
        clearTimeout(timeout);
        setSearching(false);
        setSuggestions(status === 'OK' ? predictions.slice(0, 5) : []);
      }
    );
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 400);
  };

  const handleSelect = (pred) => {
    selectingRef.current = true;
    setQuery(pred.description);
    setSuggestions([]);
    if (!geocoderRef.current) { onChange(pred.description); onSelect({ address: pred.description }); selectingRef.current = false; return; }
    geocoderRef.current.geocode({ placeId: pred.place_id }, (results, status) => {
      selectingRef.current = false;
      if (status === 'OK' && results[0]) {
        const r = results[0];
        const loc = r.geometry.location;
        const comps = r.address_components;
        const get = (type) => comps.find(c => c.types.includes(type))?.long_name || '';
        const city = get('locality') || get('administrative_area_level_2');
        const pincode = get('postal_code');
        onChange(pred.description);
        onSelect({ address: pred.description, lat: loc.lat(), lng: loc.lng(), city, pincode });
      }
    });
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          value={query}
          onChange={handleChange}
          onBlur={() => { setTimeout(() => { if (!selectingRef.current) setSuggestions([]); }, 150); }}
          placeholder={placeholder}
          className="w-full pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-blue-500 transition-colors"
        />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 animate-spin" />}
      </div>
      {suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 bg-white border border-slate-200 rounded-xl shadow-lg mt-1 overflow-hidden">
          {suggestions.map(s => (
            <button key={s.place_id} type="button"
              onPointerDown={() => { selectingRef.current = true; }}
              onClick={() => handleSelect(s)}
              className="w-full text-left px-3 py-2.5 hover:bg-blue-50 text-sm text-slate-700 border-b border-slate-100 last:border-0 flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <span className="truncate">{s.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Create Customer Modal ─────────────────────────────────────────────────────
const CreateCustomerModal = ({ token, onClose, onCreated }) => {
  const [form, setForm] = useState({
    full_name: '', phone: '', email: '',
    address_line1: '', address_line2: '', city: '', pincode: '',
    latitude: null, longitude: null,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.full_name.trim()) { toast.error('Name is required'); return; }
    if (!form.phone.trim() || form.phone.length < 10) { toast.error('Valid phone number is required'); return; }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        address_line1: form.address_line1,
        address_line2: form.address_line2,
        city: form.city,
        pincode: form.pincode,
        latitude: form.latitude,
        longitude: form.longitude,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/create-individual-customer`, payload,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Customer "${res.data.full_name}" created! Default password: phone number`);
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create customer');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between rounded-t-3xl">
          <div>
            <h3 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Add Customer</h3>
            <p className="text-xs text-slate-400 mt-0.5">Default password will be the phone number</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-500">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Full Name *</label>
            <input value={form.full_name} onChange={e => set('full_name', e.target.value)}
              placeholder="e.g. Rahul Sharma"
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
          </div>

          {/* Phone */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Phone Number *</label>
            <input value={form.phone} onChange={e => set('phone', e.target.value)}
              placeholder="10-digit mobile number" maxLength={10} type="tel"
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
          </div>

          {/* Email */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Email <span className="text-slate-300 font-normal normal-case">(optional)</span>
            </label>
            <input value={form.email} onChange={e => set('email', e.target.value)}
              placeholder="customer@email.com" type="email"
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
          </div>

          {/* Address search */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Address / Location</label>
            <AddressSearchInput
              value={form.address_line1}
              onChange={v => set('address_line1', v)}
              onSelect={({ address, lat, lng, city, pincode }) => {
                setForm(f => ({
                  ...f,
                  address_line1: address,
                  latitude: lat || null,
                  longitude: lng || null,
                  city: city || f.city,
                  pincode: pincode || f.pincode,
                }));
              }}
              placeholder="Search area, landmark, street..."
            />
          </div>

          {/* House / Floor */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">
              Flat / House / Tower / Floor <span className="text-slate-300 font-normal normal-case">(optional)</span>
            </label>
            <input value={form.address_line2} onChange={e => set('address_line2', e.target.value)}
              placeholder="e.g. Flat 4B, Tower C"
              className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
          </div>

          {/* City + Pincode */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">City</label>
              <input value={form.city} onChange={e => set('city', e.target.value)}
                placeholder="Gurugram"
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Pincode</label>
              <input value={form.pincode} onChange={e => set('pincode', e.target.value)}
                placeholder="122001" maxLength={6}
                className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:border-blue-500 transition-colors" />
            </div>
          </div>

          <button onClick={handleSubmit} disabled={saving}
            className="w-full py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {saving ? 'Creating...' : 'Create Customer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminIndividualCustomers = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('customers'); // 'customers' | 'foldpass' | 'referrals'
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [planFilter, setPlanFilter] = useState('all');
  const [activatingPlan, setActivatingPlan] = useState(null);
  const [expandedAddons, setExpandedAddons] = useState(new Set());
  const [referrals, setReferrals] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralFilter, setReferralFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingCustomer, setDeletingCustomer] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin()) { navigate('/dashboard'); return; }
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (activeTab === 'foldpass' && plans.length === 0) fetchPlans();
    if (activeTab === 'referrals' && referrals.length === 0) fetchReferrals();
  }, [activeTab]);

  const fetchReferrals = async () => {
    setReferralsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferrals(res.data.referrals || []);
      setReferralStats(res.data.stats || {});
    } catch { toast.error('Failed to load referrals'); }
    finally { setReferralsLoading(false); }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/admin/individual-customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch {
      toast.error('Failed to load individual customers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!deletingCustomer) return;
    setDeleteSubmitting(true);
    try {
      await axios.delete(`${API}/admin/individual-customers/${deletingCustomer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${deletingCustomer.full_name} deleted`);
      setDeletingCustomer(null);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const res = await axios.get(`${API}/admin/individual-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(res.data);
    } catch {
      toast.error('Failed to load FoldPass plans');
    } finally {
      setPlansLoading(false);
    }
  };

  const handleActivatePlan = async (planId) => {
    setActivatingPlan(planId);
    try {
      await axios.patch(`${API}/admin/individual-plans/${planId}/activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan activated successfully!');
      fetchPlans();
      fetchCustomers(); // refresh customer plan status too
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate plan');
    } finally {
      setActivatingPlan(null);
    }
  };

  const filtered = customers.filter(c =>
    c.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCount = customers.filter(c => c.status === 'active').length;
  const foldpassCount = customers.filter(c => c.active_plan).length;

  const filteredPlans = plans.filter(p => {
    if (planFilter === 'all') return true;
    return p.status === planFilter;
  });

  return (
    <div className="p-6 space-y-6" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Individual Customers</h1>
          <p className="text-sm text-slate-500 mt-0.5">Customers who use the individual (non-PG) plan</p>
        </div>
        <button onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-2xl transition-all shadow-sm shadow-blue-200">
          <Plus className="w-4 h-4" /> Add Customer
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{customers.length}</p>
              <p className="text-xs text-slate-400 font-medium">Total Customers</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
              <Home className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{activeCount}</p>
              <p className="text-xs text-slate-400 font-medium">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm md:col-span-1 col-span-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {customers.filter(c => {
                  const created = new Date(c.created_at);
                  const today = new Date();
                  return created.toDateString() === today.toDateString();
                }).length}
              </p>
              <p className="text-xs text-slate-400 font-medium">Registered Today</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm col-span-2 md:col-span-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
              <Repeat className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{foldpassCount}</p>
              <p className="text-xs text-slate-400 font-medium">Active FoldPass</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-2 bg-slate-100 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('customers')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
            activeTab === 'customers'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Customers
        </button>
        <button
          onClick={() => setActiveTab('foldpass')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
            activeTab === 'foldpass'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Repeat className="w-3.5 h-3.5" />
          FoldPass Plans
          {plans.length > 0 && (
            <span className="bg-violet-100 text-violet-700 text-[10px] font-black px-1.5 py-0.5 rounded-full">{plans.length}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('referrals')}
          className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
            activeTab === 'referrals'
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          <Gift className="w-3.5 h-3.5" />
          Referrals
          {referralStats?.total_count > 0 && (
            <span className="bg-orange-100 text-orange-700 text-[10px] font-black px-1.5 py-0.5 rounded-full">{referralStats.total_count}</span>
          )}
        </button>
      </div>

      {activeTab === 'customers' && <>
      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name, phone, email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="pl-9 bg-white border-slate-200 rounded-xl"
        />
      </div>

      {/* Customers list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl py-16 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3">
            <Users className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
          </div>
          <p className="font-semibold text-slate-500 mb-1">
            {searchTerm ? 'No customers found' : 'No individual customers yet'}
          </p>
          <p className="text-sm text-slate-400">Individual customers will appear here once they register</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden md:table-cell">Contact</th>
                  <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden lg:table-cell">Address</th>
                  <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden md:table-cell">Registered</th>
                  <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider">FoldPass</th>
                  <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden sm:table-cell">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((customer, i) => {
                  const initials = customer.full_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';
                  return (
                    <tr key={customer.id} className={`hover:bg-slate-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center text-xs font-black text-orange-600 flex-shrink-0">
                            {initials}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{customer.full_name}</p>
                            <p className="text-xs text-slate-400 flex items-center gap-1 md:hidden">
                              <Phone className="w-3 h-3" />{customer.phone}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <div className="space-y-0.5">
                          <p className="text-sm text-slate-700 flex items-center gap-1.5">
                            <Phone className="w-3 h-3 text-slate-400" />{customer.phone}
                          </p>
                          <p className="text-xs text-slate-400 flex items-center gap-1.5">
                            <Mail className="w-3 h-3 text-slate-400" />{customer.email}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <div className="flex items-start gap-1.5 max-w-[200px]">
                          <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-slate-600 line-clamp-2">
                            {customer.address_line2 ? `${customer.address_line2}, ` : ''}{customer.city || customer.address_line1}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <p className="text-sm text-slate-600">
                          {customer.created_at ? format(new Date(customer.created_at), 'dd MMM yyyy') : '—'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        {customer.active_plan ? (() => {
                          const plan = customer.active_plan;
                          const totalKg = plan.total_kg || 0;
                          const kgLeft = Math.max(0, totalKg - (plan.used_kg || 0));
                          const addons = plan.addons || [];
                          const addonKg = addons.reduce((sum, a) => sum + (a.kg || 0), 0);
                          const expiry = plan.expiry_date ? new Date(plan.expiry_date) : null;
                          const daysLeft = expiry ? Math.max(0, Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24))) : 0;
                          const isExpiringSoon = daysLeft <= 7;
                          return (
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5">
                                <Repeat className="w-3 h-3 text-violet-600" />
                                <span className="text-xs font-bold text-violet-700">{plan.plan_name || plan.plan_type}</span>
                              </div>
                              <p className="text-[11px] text-slate-500 font-medium">{kgLeft.toFixed(1)} kg left of {totalKg} kg</p>
                              {addonKg > 0 && (
                                <p className="text-[10px] font-bold text-orange-500">+{addonKg} kg add-on included</p>
                              )}
                              <p className={`text-[10px] font-semibold ${isExpiringSoon ? 'text-red-500' : 'text-slate-400'}`}>
                                Expires {expiry ? format(expiry, 'dd MMM yyyy') : '—'}{isExpiringSoon ? ` (${daysLeft}d)` : ''}
                              </p>
                            </div>
                          );
                        })() : (
                          <span className="text-[11px] text-slate-300 font-medium">No plan</span>
                        )}
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell">
                        <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full border ${
                          customer.status === 'active'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-red-50 text-red-700 border-red-200'
                        }`}>
                          {customer.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setDeletingCustomer(customer)}
                          className="text-xs font-semibold px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition-all">
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </> }

      {/* FoldPass Plans Tab */}
      {activeTab === 'foldpass' && (
        <div className="space-y-4">
          {/* Plan filter tabs */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: 'All Plans' },
              { key: 'active', label: 'Active' },
              { key: 'pending_payment', label: 'Pending Payment' },
              { key: 'exhausted', label: 'Exhausted' },
              { key: 'expired', label: 'Expired' },
              { key: 'renewed', label: 'Renewed' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setPlanFilter(f.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${
                  planFilter === f.key
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'
                }`}
              >
                {f.label}
                {f.key === 'all' ? ` (${plans.length})` :
                  ` (${plans.filter(p => p.status === f.key).length})`}
              </button>
            ))}
          </div>

          {plansLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl py-16 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-violet-50 rounded-full flex items-center justify-center mb-3">
                <Repeat className="w-8 h-8 text-violet-300" strokeWidth={1.5} />
              </div>
              <p className="font-semibold text-slate-500 mb-1">No FoldPass plans found</p>
              <p className="text-sm text-slate-400">Plans will appear here once customers subscribe</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider">Customer</th>
                      <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider">Plan</th>
                      <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden md:table-cell">KG Usage</th>
                      <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden lg:table-cell">Add-ons</th>
                      <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider hidden md:table-cell">Expiry</th>
                      <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider">Payment</th>
                      <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-6 py-4 text-[11px] font-black text-slate-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredPlans.map((plan, i) => {
                      const customer = plan.customer_info || {};
                      const displayName = customer.full_name || plan.customer_name || '—';
                      const displayPhone = customer.phone || '—';
                      const initials = displayName !== '—'
                        ? displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
                        : 'U';
                      const usedKg = plan.used_kg || 0;
                      const totalKg = plan.total_kg || 0;
                      const addons = plan.addons || [];
                      const addonKg = addons.reduce((sum, a) => sum + (a.kg || 0), 0);
                      const baseKg = totalKg - addonKg;
                      const kgPct = totalKg > 0 ? Math.min(100, (usedKg / totalKg) * 100) : 0;
                      const kgLeft = Math.max(0, totalKg - usedKg);
                      const expiry = plan.expiry_date ? new Date(plan.expiry_date) : null;
                      const daysLeft = expiry ? Math.max(0, Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24))) : null;
                      const isExpiringSoon = daysLeft !== null && daysLeft <= 7 && plan.status === 'active';
                      const isAddonsExpanded = expandedAddons.has(plan.id);
                      const statusConfig = {
                        active: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle className="w-3 h-3" />, label: 'Active' },
                        pending_payment: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: <Clock className="w-3 h-3" />, label: 'Pending' },
                        exhausted: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: <XCircle className="w-3 h-3" />, label: 'Exhausted' },
                        expired: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', icon: <XCircle className="w-3 h-3" />, label: 'Expired' },
                        renewed: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', icon: <CheckCircle className="w-3 h-3" />, label: 'Renewed' },
                      };
                      const sc = statusConfig[plan.status] || statusConfig.active;
                      return (
                        <tr key={plan.id} className={`hover:bg-slate-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                          {/* Customer */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-violet-100 rounded-xl flex items-center justify-center text-xs font-black text-violet-600 flex-shrink-0">
                                {initials}
                              </div>
                              <div>
                                <p className="font-semibold text-slate-800 text-sm">{displayName}</p>
                                <p className="text-xs text-slate-400">{displayPhone}</p>
                              </div>
                            </div>
                          </td>
                          {/* Plan */}
                          <td className="px-6 py-4">
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-1.5">
                                <p className="font-bold text-sm text-violet-700">{plan.plan_name || plan.plan_type}</p>
                                {plan.is_renewal && (
                                  <span className="text-[9px] font-black bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">RENEWAL</span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">{plan.base_kg || baseKg} kg base · ₹{plan.price}</p>
                              {(plan.carry_over_kg > 0) && (
                                <p className="text-[10px] font-bold text-blue-600">+{plan.carry_over_kg} kg carried over</p>
                              )}
                              {addonKg > 0 && (
                                <p className="text-[10px] font-bold text-orange-600">+{addonKg} kg add-on → {totalKg} kg total</p>
                              )}
                            </div>
                          </td>
                          {/* KG Usage */}
                          <td className="px-6 py-4 hidden md:table-cell">
                            <div className="w-32 space-y-1.5">
                              <div className="flex justify-between text-[10px] font-semibold">
                                <span className="text-slate-500">{usedKg.toFixed(1)} kg used</span>
                                <span className={kgLeft <= 2 ? 'text-red-500' : 'text-emerald-600'}>{kgLeft.toFixed(1)} left</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all duration-700 ${
                                    kgPct >= 90 ? 'bg-red-500' : kgPct >= 60 ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`}
                                  style={{ width: `${kgPct}%` }}
                                />
                              </div>
                              <p className="text-[10px] text-slate-400">{Math.round(kgPct)}% of {totalKg} kg used</p>
                            </div>
                          </td>
                          {/* Add-ons */}
                          <td className="px-6 py-4 hidden lg:table-cell">
                            {addons.length === 0 ? (
                              <span className="text-xs text-slate-300">None</span>
                            ) : (
                              <div className="space-y-1">
                                <button
                                  onClick={() => setExpandedAddons(prev => {
                                    const next = new Set(prev);
                                    if (next.has(plan.id)) next.delete(plan.id); else next.add(plan.id);
                                    return next;
                                  })}
                                  className="flex items-center gap-1 text-[10px] font-bold text-orange-600 bg-orange-50 border border-orange-200 px-2 py-1 rounded-full hover:bg-orange-100 transition-colors"
                                >
                                  <Plus className="w-2.5 h-2.5" />
                                  {addons.length} add-on{addons.length > 1 ? 's' : ''} · +{addonKg} kg
                                  {isAddonsExpanded ? <ChevronUp className="w-2.5 h-2.5 ml-0.5" /> : <ChevronDown className="w-2.5 h-2.5 ml-0.5" />}
                                </button>
                                {isAddonsExpanded && (
                                  <div className="space-y-1 mt-1.5">
                                    {addons.map((addon, ai) => (
                                      <div key={ai} className="flex items-center gap-1.5 text-[10px] text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-2 py-1">
                                        <span className="font-bold text-orange-500">+{addon.kg} kg</span>
                                        <span className="text-slate-400">·</span>
                                        <span>₹{addon.price}</span>
                                        {addon.added_at && (
                                          <>
                                            <span className="text-slate-400">·</span>
                                            <span className="text-slate-400">{format(new Date(addon.added_at), 'dd MMM')}</span>
                                          </>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                          {/* Expiry */}
                          <td className="px-6 py-4 hidden md:table-cell">
                            {expiry ? (
                              <div>
                                <p className={`text-sm font-medium ${isExpiringSoon ? 'text-red-600' : 'text-slate-700'}`}>
                                  {format(expiry, 'dd MMM yyyy')}
                                </p>
                                {daysLeft !== null && plan.status === 'active' && (
                                  <p className={`text-[10px] font-semibold mt-0.5 ${isExpiringSoon ? 'text-red-500' : 'text-slate-400'}`}>
                                    {daysLeft === 0 ? 'Expires today' : `${daysLeft}d remaining`}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          {/* Payment */}
                          <td className="px-6 py-4">
                            {(plan.payment_status === 'paid' || (!plan.payment_status && plan.status !== 'pending_payment')) ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <CheckCircle className="w-3 h-3" /> Paid
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                <Clock className="w-3 h-3" /> Pending
                              </span>
                            )}
                          </td>
                          {/* Status */}
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full border ${sc.bg} ${sc.text} ${sc.border}`}>
                              {sc.icon} {sc.label}
                            </span>
                          </td>
                          {/* Action */}
                          <td className="px-6 py-4">
                            {plan.status === 'pending_payment' ? (
                              <button
                                onClick={() => handleActivatePlan(plan.id)}
                                disabled={activatingPlan === plan.id}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-60"
                              >
                                {activatingPlan === plan.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <CreditCard className="w-3 h-3" />
                                )}
                                Activate (Cash)
                              </button>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 border-t border-slate-100 bg-slate-50">
                <p className="text-xs text-slate-400">{filteredPlans.length} plan{filteredPlans.length !== 1 ? 's' : ''} shown</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Referrals Tab ── */}
      {activeTab === 'referrals' && (
        <div className="space-y-4">
          {/* Stats */}
          {referralStats && (
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Total Referrals', value: referralStats.total_count || 0, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-100' },
                { label: 'Active Referrers', value: referralStats.active_referrers || 0, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100' },
                { label: 'Total Discount Given', value: `₹${(referralStats.total_discount_given || 0).toFixed(0)}`, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
              ].map(s => (
                <div key={s.label} className={`${s.bg} border rounded-2xl px-5 py-4 text-center`}>
                  <p className={`text-2xl font-black ${s.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{s.value}</p>
                  <p className="text-xs text-slate-500 font-semibold mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Filter pills */}
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: 'All' },
              { key: 'registered', label: 'Registered' },
              { key: 'first_order_placed', label: 'First Order Placed' },
              { key: 'reward_applied', label: 'Reward Applied' },
            ].map(f => (
              <button key={f.key} onClick={() => setReferralFilter(f.key)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                  referralFilter === f.key
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            {referralsLoading ? (
              <div className="py-16 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Referrer</th>
                      <th className="text-left px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest"></th>
                      <th className="text-left px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Referee</th>
                      <th className="text-left px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                      <th className="text-left px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Registered</th>
                      <th className="text-left px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">First Order</th>
                      <th className="text-left px-5 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">Discounts</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {referrals
                      .filter(r => referralFilter === 'all' || r.status === referralFilter)
                      .map(r => {
                        const statusConfig = {
                          registered: { label: 'Registered', cls: 'bg-amber-100 text-amber-700' },
                          first_order_placed: { label: 'First Order', cls: 'bg-blue-100 text-blue-700' },
                          reward_applied: { label: 'Reward Applied', cls: 'bg-emerald-100 text-emerald-700' },
                        }[r.status] || { label: r.status, cls: 'bg-slate-100 text-slate-600' };

                        return (
                          <tr key={r.id} className="hover:bg-slate-50/60 transition-colors">
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-black text-xs flex-shrink-0">
                                  {r.referrer_name?.[0]?.toUpperCase() || '?'}
                                </div>
                                <span className="text-sm font-semibold text-slate-800">{r.referrer_name || '—'}</span>
                              </div>
                            </td>
                            <td className="px-2 py-3">
                              <ArrowRight className="w-4 h-4 text-slate-300" />
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-black text-xs flex-shrink-0">
                                  {r.referee_name?.[0]?.toUpperCase() || '?'}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-slate-800">{r.referee_name || '—'}</p>
                                  <p className="text-xs text-slate-400">{r.referee_phone}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-3">
                              <span className={`px-2.5 py-1 rounded-full text-[10px] font-black ${statusConfig.cls}`}>{statusConfig.label}</span>
                            </td>
                            <td className="px-5 py-3 text-xs text-slate-500">
                              {r.registered_at ? format(new Date(r.registered_at), 'dd MMM yy') : '—'}
                            </td>
                            <td className="px-5 py-3 text-xs text-slate-500">
                              {r.first_order_at ? format(new Date(r.first_order_at), 'dd MMM yy') : '—'}
                            </td>
                            <td className="px-5 py-3">
                              <div className="flex items-center gap-1">
                                <span className="bg-blue-50 text-blue-700 text-[10px] font-black px-2 py-0.5 rounded-full">{r.referee_discount_pct || 10}% off</span>
                                <ArrowRight className="w-3 h-3 text-slate-300" />
                                <span className="bg-orange-50 text-orange-700 text-[10px] font-black px-2 py-0.5 rounded-full">{r.referrer_discount_pct || 5}% reward</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    {referrals.filter(r => referralFilter === 'all' || r.status === referralFilter).length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-16 text-center">
                          <Gift className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                          <p className="text-sm text-slate-400 font-semibold">No referrals {referralFilter !== 'all' ? `with status "${referralFilter}"` : 'yet'}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50">
              <p className="text-xs text-slate-400">
                {referrals.filter(r => referralFilter === 'all' || r.status === referralFilter).length} referral(s) shown
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Create Customer Modal */}
      {showCreateModal && (
        <CreateCustomerModal
          token={token}
          onClose={() => setShowCreateModal(false)}
          onCreated={(newCustomer) => {
            setCustomers(prev => [newCustomer, ...prev]);
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Delete Customer Confirmation */}
      {deletingCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Delete Customer?</h3>
                <p className="text-xs text-slate-500">This cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-2">
              You are about to delete <span className="font-bold text-slate-800">{deletingCustomer.full_name}</span>.
            </p>
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2 mb-5">
              All orders, chat messages, FoldPass plans, and referral records for this customer will also be permanently deleted.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setDeletingCustomer(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={handleDeleteCustomer} disabled={deleteSubmitting}
                className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-1.5">
                {deleteSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminIndividualCustomers;
